import {Component, forwardRef, inject, Input, OnInit} from '@angular/core';
import {FieldValidationService} from '../../core/field-validation.service';
import {AbstractControl, ControlValueAccessor, FormGroup, NG_VALUE_ACCESSOR, ValidatorFn} from '@angular/forms';
import {FieldParameters} from '../../core/definitions/field-parameters';
import {System} from "../../core/definitions/system";
import {FieldValidators} from "../../core/field-validators";
import {msToTimecode, timecodeToMs} from "./timecode.utils";

@Component({
  selector: 'app-edit-field-timecode',
  templateUrl: './edit-field-timecode.component.html',
  styleUrls: ['./edit-field-timecode.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => EditFieldTimecodeComponent)
    }
  ],
  standalone: false
})
export class EditFieldTimecodeComponent implements OnInit, ControlValueAccessor {
  private readonly fieldValidation: FieldValidationService = inject(FieldValidationService);

  @Input() fieldParameters: FieldParameters;
  @Input() field;
  @Input() formControlName;
  @Input() placeholder;

  inFocus: boolean;
  displayValue = '';

  private onChange: (value: number | null) => void = () => {
  };
  private onTouched: () => void = () => {
  };

  private intervalValidator?: ValidatorFn;

  ngOnInit() {
    const formGroup = this.fieldParameters.sectionsContainer.formGroup as FormGroup;
    const control = formGroup.get(this.formControlName);
    if (!control) return;

    // Initialize display value from form control
    if (Number.isFinite(control.value)) {
      this.displayValue = msToTimecode(control.value);
    }

    // Add control validator
    control.addValidators(FieldValidators.validateTimecode());

    const keys = this.getIntervalKeys();

    if (keys) {
      const related = this.getRelatedControl(formGroup, keys);
      if (related) {
        this.ensureIntervalValidator(formGroup, keys.startKey, keys.endKey);

        if (keys.isStart) {
          control.addValidators(
            FieldValidators.validateTimecodeInterval(keys.startKey, keys.endKey, true)
          );
        }

        control.valueChanges.subscribe(() => {
          related.updateValueAndValidity({onlySelf: true, emitEvent: false});
          formGroup.updateValueAndValidity({onlySelf: true, emitEvent: false});
        });

        related.valueChanges.subscribe(() => {
          control.updateValueAndValidity({onlySelf: true, emitEvent: false});
          formGroup.updateValueAndValidity({onlySelf: true, emitEvent: false});
        });
      }
    }

    control.updateValueAndValidity({onlySelf: true, emitEvent: false});
    formGroup.updateValueAndValidity({onlySelf: true, emitEvent: false});
  }

  isInvalid() {
    return this.fieldValidation.isFieldInvalid(this.fieldParameters.sectionsContainer, this.formControlName);
  }

  onFieldBlur(event: Event) {
    this.inFocus = false;
    const input = event.target as HTMLInputElement;
    const ms = timecodeToMs(input.value);
    // Preserve invalid text so the user can see and correct it
    if (ms !== null) {
      this.displayValue = msToTimecode(ms);
      input.value = this.displayValue;
    }
    this.onTouched();
  }

  onFieldFocus() {
    if (!this.fieldParameters.sectionsContainer.isDialog) {
      this.inFocus = true;
    }
  }

  onValueInput(event: Event) {
    const rawValue = (event.target as HTMLInputElement).value;
    this.displayValue = rawValue;

    // NaN intentionally reaches the control so validateTimecode() can reject it
    const value: number | null =
      rawValue.trim() === '' ? null : timecodeToMs(rawValue) ?? Number.NaN;

    this.onChange(value);
    // Never write NaN to the model - the invalid control blocks saving instead
    this.fieldParameters.object[this.field.name] = Number.isFinite(value) ? value : null;
  }

  writeValue(value: any): void {
    if (Number.isFinite(value)) {
      this.displayValue = msToTimecode(value);
    } else {
      this.displayValue = '';
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
  }

  private getIntervalKeys(): { startKey: string; endKey: string; isStart: boolean } | null {
    const key = String(this.formControlName ?? '');
    const sep = System.keySep;
    const lastSep = key.lastIndexOf(sep);

    const leaf = lastSep === -1 ? key : key.substring(lastSep + sep.length);
    if (leaf !== 'start' && leaf !== 'end') return null;

    const parent = lastSep === -1 ? '' : key.substring(0, lastSep);
    const startKey = parent ? `${parent}${sep}start` : 'start';
    const endKey = parent ? `${parent}${sep}end` : 'end';

    return {startKey, endKey, isStart: leaf === 'start'};
  }

  private getRelatedControl(formGroup: FormGroup, keys: {
    startKey: string;
    endKey: string;
    isStart: boolean
  }): AbstractControl | null {
    return formGroup.get(keys.isStart ? keys.endKey : keys.startKey);
  }

  private ensureIntervalValidator(formGroup: FormGroup, startName: string, endName: string) {
    // Prevent duplicate validator
    const marker = `__timecode_interval__${startName}__${endName}`;
    const fgAny = formGroup as any;
    if (fgAny[marker]) return;

    this.intervalValidator = FieldValidators.validateTimecodeInterval(startName, endName);
    formGroup.addValidators(this.intervalValidator);
    fgAny[marker] = true;
  }
}
